import { Splide } from '@hh/global.js'
import {
  getStickyHeaderOffsetPx,
  isCollectionFiltersOrSortApplied,
  isScrollTargetElementVisible,
  resolveScrollTargetElement,
  scrollPageToElementWithHeaderOffset
} from '@hh/component-anchor-target-utils.js'

/** Splide track horizontal padding: 16px (mobile) / 24px (desktop, min-width 990px). */
function sideTrackPaddingPx() {
  return window.innerWidth < 990 ? 16 : 24
}

const anchorCarousels = new Set()

function setArrowButtonActive(button, isActive) {
  if (!button) {
    return
  }
  if (isActive) {
    button.removeAttribute('aria-disabled')
    button.removeAttribute('tabindex')
  } else {
    button.setAttribute('aria-disabled', 'true')
    button.setAttribute('tabindex', '-1')
  }
}

/**
 * @param {Element | null} [list]
 */
function getListTranslateX(list) {
  const el = list
  if (!el) {
    return 0
  }
  const t = getComputedStyle(el).transform
  if (!t || t === 'none') {
    return 0
  }
  return new DOMMatrix(t).m41
}

/**
 * @param {Splide} splide
 */
function updateCustomArrowState(splide) {
  const root = splide.root
  if (!root) {
    return
  }
  const unit = root.closest('[data-anchor-carousel-slider-unit]')
  const nextBtn = unit?.querySelector('[data-anchor-carousel-next]')
  const prevBtn = unit?.querySelector('[data-anchor-carousel-prev]')
  const list = root.querySelector('.splide__list')
  const track = root.querySelector('.splide__track')
  if (!list || !track) {
    return
  }
  const epsilon = 6
  const m41 = getListTranslateX(list)
  const listWidth = list.scrollWidth
  const viewWidth = track.clientWidth
  const maxS = Math.max(0, listWidth - viewWidth)
  if (maxS < epsilon) {
    setArrowButtonActive(prevBtn, false)
    setArrowButtonActive(nextBtn, false)
    unit?.classList.add('hide-scroll-buttons')
    return
  }
  const atStart = m41 >= -epsilon
  const atEnd = m41 <= -maxS + epsilon
  setArrowButtonActive(prevBtn, !atStart)
  setArrowButtonActive(nextBtn, !atEnd)
  unit?.classList.toggle('hide-scroll-buttons', atStart && atEnd)
}

/**
 * @param {Splide} splide
 */
function syncInViewFromSplide(splide) {
  const root = splide.root
  if (!root) {
    return
  }
  for (const host of root.querySelectorAll(
    '[data-anchor-carousel-slide]:not([hidden])'
  )) {
    const li = host.closest('li.splide__slide')
    host.classList.toggle('in-view', Boolean(li?.classList.contains('is-active')))
  }
}

/**
 * Where to re-insert a slide in `.splide__list` (Liquid `data-slide-index` order).
 * @param {Splide} splide
 * @param {HTMLElement} host
 */
function computeAtelierSlideInsertIndex(splide, host) {
  const myIdx = Number.parseInt(host.getAttribute('data-slide-index') ?? '0', 10) || 0
  const list = splide.root?.querySelector('.splide__list')
  if (!list) {
    return 0
  }
  const items = [
    ...list.querySelectorAll(':scope > .splide__slide:not(.splide__slide--clone)'),
  ]
  let insert = 0
  for (const li of items) {
    const h = li.querySelector('anchor-carousel-atelier-slide[data-slide-index]')
    const o = Number.parseInt(h?.getAttribute('data-slide-index') ?? '0', 10) || 0
    if (o < myIdx) {
      insert += 1
    } else {
      break
    }
  }
  return insert
}

/**
 * `splide.remove(HTMLElement)` is not handled as “remove this node” in Splide 4 — non-function
 * matchers are compared to slide *indices* (`includes([el], index)`), so a bare `li` never matches.
 * Remove by the internal slide’s numeric `index` instead of `splide.remove((s) => s.slide === li)`.
 * @param {import('@hh/global.js').Splide} splide
 * @param {HTMLLIElement} li
 * @returns {boolean}
 */
function removeAtelierListItemFromSplide(splide, li) {
  const slideComps = splide.Components?.Slides?.get(true)
  if (!Array.isArray(slideComps)) {
    return false
  }
  const comp = slideComps.find((s) => s.slide === li)
  if (!comp || typeof comp.index !== 'number') {
    return false
  }
  try {
    splide.remove(comp.index)
    return true
  } catch {
    return false
  }
}

/**
 * Slides that stored `data-splide-omit-pending` (Splide not mounted yet) or only had `li[hidden]`:
 * call `omitFromSplideCarousel` so Splide drops them from its slide list.
 * @param {{ root: HTMLElement, splide: object }} carousel
 */
function syncPendingOmittedAtelierSlidesAfterMount(carousel) {
  if (isAnchorAtelierPlpHidingFullSection()) {
    return
  }
  const { root, splide } = carousel
  if (!splide || !root) {
    return
  }
  for (const host of root.querySelectorAll(
    'anchor-carousel-atelier-slide[data-splide-omit-pending]',
  )) {
    if (
      host instanceof HTMLElement &&
      typeof host.omitFromSplideCarousel === 'function'
    ) {
      host.omitFromSplideCarousel()
    }
  }
  for (const li of root.querySelectorAll(
    '.splide__list > .splide__slide[hidden]:not(.splide__slide--clone)',
  )) {
    const h = li.querySelector('anchor-carousel-atelier-slide[data-anchor-carousel-slide]')
    if (h instanceof HTMLElement && typeof h.omitFromSplideCarousel === 'function') {
      h.omitFromSplideCarousel()
    }
  }
}

class AnchorAtelierSplideCarousel {
  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.root = root
    this.sliderUnit = root.closest('[data-anchor-carousel-slider-unit]')
    this.buttonNext = this.sliderUnit?.querySelector(
      '[data-anchor-carousel-next]'
    )
    this.buttonPrev = this.sliderUnit?.querySelector(
      '[data-anchor-carousel-prev]'
    )

    /** 1.5 slides on narrow viewports; 5.5 on desktop. Few-slide carousels (`data-anchor-mobile-only`) stay at 1.5. */
    const perPageOptions = {
      perPage: 1.5,
      ...(root.hasAttribute('data-anchor-mobile-only')
        ? {}
        : {
            breakpoints: {
              990: {
                perPage: 5.5,
                focus: 0,
              },
            },
          }),
    }

    this.splide = new Splide(root, {
      type: 'slide',
      // Splide defaults to max-width media queries; `min` = breakpoint keys are min-width (1.5 default, 5.5 from 990px up).
      mediaQuery: 'min',
      // Mobile uses centered focus; desktop switches to left-aligned focus via breakpoint.
      focus: 'center',
      perMove: 1,
      gap: '12px',
      arrows: false,
      pagination: false,
      speed: 400,
      padding: {
        left: sideTrackPaddingPx(),
        right: sideTrackPaddingPx(),
      },
      ...perPageOptions,
    })
    this._onMoved = () => {
      this.refreshUi()
    }
    this._resizeT = null
    this._onWindowResize = () => {
      clearTimeout(this._resizeT)
      this._resizeT = setTimeout(() => {
        this._resizeT = null
        this.applyPaddingAndCentering()
        this.refreshUi()
      }, 150)
    }
    /** Slides removed from Splide (detached from document) — still need `refreshCta` on PLP updates. */
    this.omittedSlideHosts = new Set()
  }

  refreshUi() {
    requestAnimationFrame(() => {
      if (!this.splide?.root?.isConnected) {
        return
      }
      syncInViewFromSplide(this.splide)
      updateCustomArrowState(this.splide)
    })
  }

  applyPaddingAndCentering() {
    this.applyBasePadding()
  }

  applyBasePadding() {
    const p = sideTrackPaddingPx()
    // Do not spread `this.splide.options` — it flattens breakpoint-merged values (e.g. perPage) into base.
    this.splide.options = { padding: { left: p, right: p } }
    this.splide.refresh()
  }

  mount() {
    this.splide.mount()
    this.splide.on('moved', this._onMoved)
    this.splide.on('updated', this._onMoved)
    window.addEventListener('resize', this._onWindowResize, { passive: true })

    this.buttonNext?.addEventListener('click', this._onNext)
    this.buttonPrev?.addEventListener('click', this._onPrev)

    this.applyPaddingAndCentering()
    this.refreshUi()
    syncPendingOmittedAtelierSlidesAfterMount(this)
  }

  _onNext = (e) => {
    e.preventDefault()
    if (this.buttonNext?.getAttribute('aria-disabled') === 'true') {
      return
    }
    this.splide.go('>')
  }

  _onPrev = (e) => {
    e.preventDefault()
    if (this.buttonPrev?.getAttribute('aria-disabled') === 'true') {
      return
    }
    this.splide.go('<')
  }

  destroy() {
    this.splide.off('moved', this._onMoved)
    this.splide.off('updated', this._onMoved)
    window.removeEventListener('resize', this._onWindowResize)
    this.buttonNext?.removeEventListener('click', this._onNext)
    this.buttonPrev?.removeEventListener('click', this._onPrev)
    if (this._resizeT) {
      clearTimeout(this._resizeT)
    }
    this.splide.destroy(true)
    this.omittedSlideHosts.clear()
    this.root.removeAttribute('data-anchor-carousel-mounted')
    this.root.removeAttribute('data-splide-mounted')
    delete this.root._anchorSplide
    anchorCarousels.delete(this)
  }

  resync() {
    this.applyPaddingAndCentering()
    this.refreshUi()
  }
}

/** Aligned with `.anchor-carousel-atelier__slider-mobile { display: none }` in `section-anchor-carousel-atelier.css`. */
const ANCHOR_ATELIER_MOBILE_ONLY_SLIDER_HIDDEN_MQ = '(min-width: 990px)'

/**
 * The few-slide Splide in `.anchor-carousel-atelier__slider-mobile` is `display: none` on desktop.
 * Mounting Splide with zero width breaks layout and keeps a `resize` listener; defer until the viewport
 * is narrow enough for that block to be visible, and tear down when the user returns to desktop.
 * @param {HTMLElement} root
 * @returns {boolean}
 */
function isAtelierMobileOnlySplideRootDeferred(root) {
  if (!root.hasAttribute('data-anchor-mobile-only')) {
    return false
  }
  if (!root.closest('.anchor-carousel-atelier__slider-mobile')) {
    return false
  }
  return window.matchMedia(ANCHOR_ATELIER_MOBILE_ONLY_SLIDER_HIDDEN_MQ).matches
}

function mountAnchorAtelierSplideNow(root) {
  if (root.hasAttribute('data-anchor-carousel-mounted') || root._anchorSplide) {
    return
  }
  root.setAttribute('data-anchor-carousel-mounted', '1')
  const car = new AnchorAtelierSplideCarousel(root)
  root._anchorSplide = car
  anchorCarousels.add(car)
  car.mount()
}

function initAnchorAtelierSplide(root) {
  if (root.hasAttribute('data-anchor-carousel-mounted') || root._anchorSplide) {
    return
  }
  if (isAtelierMobileOnlySplideRootDeferred(root)) {
    return
  }
  mountAnchorAtelierSplideNow(root)
}

function reconcileAtelierMobileOnlySplide() {
  for (const c of [...anchorCarousels]) {
    if (!c?.root) {
      continue
    }
    if (!c.root.isConnected) {
      c.destroy()
      continue
    }
    if (
      c.root.hasAttribute('data-anchor-mobile-only') &&
      c.root.closest('.anchor-carousel-atelier__slider-mobile') &&
      window.matchMedia(ANCHOR_ATELIER_MOBILE_ONLY_SLIDER_HIDDEN_MQ).matches
    ) {
      c.destroy()
    }
  }
  mountAllAnchorAtelierSplides()
}

let atelierMobileOnlySplideMqBound = false
function ensureAtelierMobileOnlySplideMql() {
  if (atelierMobileOnlySplideMqBound) {
    return
  }
  atelierMobileOnlySplideMqBound = true
  const mql = window.matchMedia(ANCHOR_ATELIER_MOBILE_ONLY_SLIDER_HIDDEN_MQ)
  mql.addEventListener('change', () => {
    reconcileAtelierMobileOnlySplide()
  })
}

function resyncAllAnchorCarousels() {
  for (const c of [...anchorCarousels]) {
    if (c.root?.isConnected && c.splide) {
      c.resync()
    } else {
      c.destroy()
    }
  }
}

let atelierResyncRaf = null
function scheduleAnchorAtelierSlidersResync() {
  if (atelierResyncRaf != null) {
    return
  }
  atelierResyncRaf = requestAnimationFrame(() => {
    atelierResyncRaf = null
    resyncAllAnchorCarousels()
  })
}

function mountAllAnchorAtelierSplides() {
  for (const el of document.querySelectorAll(
    '[data-anchor-atelier-splide]:not([data-anchor-carousel-mounted])'
  )) {
    if (el instanceof HTMLElement) {
      initAnchorAtelierSplide(el)
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    mountAllAnchorAtelierSplides()
    ensureAtelierMobileOnlySplideMql()
  })
} else {
  mountAllAnchorAtelierSplides()
  ensureAtelierMobileOnlySplideMql()
}

/**
 * @param {string} sel
 * @returns {Element | null}
 */
function querySelectorSafe(sel) {
  if (!sel) {
    return null
  }
  try {
    return document.querySelector(sel)
  } catch {
    return null
  }
}

function resolveAnchorCarouselAtelierScrollTarget(link) {
  const target = resolveScrollTargetElement(link)
  if (target) {
    return target
  }
  const sel = link.getAttribute('data-scroll-target')?.trim()
  if (!sel) {
    return null
  }
  const bySel = querySelectorSafe(sel)
  if (bySel && isScrollTargetElementVisible(bySel)) {
    return bySel
  }
  return null
}


function syncAnchorAtelierCollectionFiltersOrSortApplied(params) {
  const r = document.querySelector('[data-anchor-carousel-atelier-root]')
  if (!r) {
    return false
  }
  const defaultSort = r.dataset.collectionDefaultSortBy ?? ''
  return isCollectionFiltersOrSortApplied(params, defaultSort)
}

/**
 * Same conditions as `syncAnchorAtelierSectionVisibility` — the whole PLP section is hidden (Liquid lines up).
 * When true, we do not per-slide remove/hide: the user only sees the empty section state, not a broken strip.
 */
function isAnchorAtelierPlpHidingFullSection() {
  return syncAnchorAtelierCollectionFiltersOrSortApplied(
    new URLSearchParams(window.location.search)
  )
}

function syncAnchorAtelierSectionVisibility() {
  const root = document.querySelector('[data-anchor-carousel-atelier-root]')
  if (!root) {
    return
  }
  const sectionEl = root.closest('.shopify-section')
  if (!sectionEl) {
    return
  }
  const params = new URLSearchParams(window.location.search)
  sectionEl.hidden = syncAnchorAtelierCollectionFiltersOrSortApplied(params)
}

function refreshAllAnchorAtelierSlidesCta() {
  for (const host of document.querySelectorAll(
    'anchor-carousel-atelier-slide[data-anchor-carousel-slide]',
  )) {
    if (
      host instanceof HTMLElement &&
      typeof host.refreshCta === 'function'
    ) {
      host.refreshCta()
    }
  }
}

function syncAnchorAtelierVisibilityAndSlides() {
  syncAnchorAtelierSectionVisibility()
}

function ensureAnchorAtelierLocationChangeEvents() {
  if (window.__anchorAtelierLocationChangePatched) {
    return
  }
  window.__anchorAtelierLocationChangePatched = true

  const dispatch = () => window.dispatchEvent(new Event('locationchange'))
  const rawPushState = history.pushState
  const rawReplaceState = history.replaceState

  history.pushState = function (...args) {
    const ret = rawPushState.apply(this, args)
    dispatch()
    return ret
  }
  history.replaceState = function (...args) {
    const ret = rawReplaceState.apply(this, args)
    dispatch()
    return ret
  }
  window.addEventListener('popstate', dispatch)
}

class AnchorCarouselAtelierSlide extends HTMLElement {
  _onLinkClick = (event) => {
    const link = event.currentTarget
    if (!(link instanceof HTMLAnchorElement)) {
      return
    }
    const target = resolveAnchorCarouselAtelierScrollTarget(link)

    if (!target) {
      return
    }
    event.preventDefault()
    
    scrollPageToElementWithHeaderOffset(target)
  }

  connectedCallback() {
    this._cachedSplideRoot =
      this.closest('[data-anchor-atelier-splide]') ?? this._cachedSplideRoot
    this.refreshCta()
  }

  disconnectedCallback() {
    const link = this.querySelector('[data-anchor-carousel-link]')
    if (link) {
      link.removeEventListener('click', this._onLinkClick)
    }
  }

  /**
   * While detached, `closest` cannot find the splide root — use `_cachedSplideRoot`.
   */
  omitFromSplideCarousel() {
    if (isAnchorAtelierPlpHidingFullSection()) {
      this.removeAttribute('data-splide-omit-pending')
      this.restoreFromSplideCarousel()
      return
    }
    this._cachedSplideRoot =
      this._cachedSplideRoot ?? this.closest('[data-anchor-atelier-splide]')
    const li = this.closest('li.splide__slide')
    if (!li) {
      return
    }
    const splide = this._cachedSplideRoot?._anchorSplide?.splide
    if (!splide) {
      li.setAttribute('hidden', '')
      this.setAttribute('data-splide-omit-pending', '')
      return
    }
    if (this._omittedFromSplide) {
      return
    }
    this.removeAttribute('data-splide-omit-pending')
    if (!removeAtelierListItemFromSplide(splide, li)) {
      li.setAttribute('hidden', '')
      this.setAttribute('data-splide-omit-pending', '')
      return
    }
    this._omittedFromSplide = true
    this._cachedSplideRoot?._anchorSplide?.omittedSlideHosts?.add(this)
  }

  /**
   * Re-add a previously removed slide at the index implied by `data-slide-index`, or only clear `hidden` on the `li`.
   */
  restoreFromSplideCarousel() {
    this._cachedSplideRoot =
      this._cachedSplideRoot ?? this.closest('[data-anchor-atelier-splide]')
    this.removeAttribute('data-splide-omit-pending')
    if (this._omittedFromSplide) {
      const car = this._cachedSplideRoot?._anchorSplide
      const splide = car?.splide
      const li = this.parentNode
      // `omitFromSplideCarousel` bails when `_omittedFromSplide` is true, so we must clear this
      // *before* `add`. `add` re-attaches the host; `connectedCallback` → `refreshCta` may
      // re-omit the slide — then only gate `omittedSlideHosts` / unhide on `!_omittedFromSplide`.
      this._omittedFromSplide = false
      if (splide && li instanceof HTMLLIElement && li.classList.contains('splide__slide')) {
        const idx = computeAtelierSlideInsertIndex(splide, this)
        try {
          splide.add(li, idx)
          if (!this._omittedFromSplide) {
            car?.omittedSlideHosts?.delete(this)
          }
        } catch {
          this._omittedFromSplide = true
          this.setAttribute('data-splide-omit-pending', '')
        }
      } else {
        this._omittedFromSplide = true
        this.setAttribute('data-splide-omit-pending', '')
      }
      if (!this._omittedFromSplide) {
        if (li instanceof HTMLLIElement) {
          li.removeAttribute('hidden')
        }
        this.removeAttribute('hidden')
      }
      return
    }
    const liInList = this.closest('li.splide__slide')
    if (liInList) {
      liInList.removeAttribute('hidden')
    }
    this.removeAttribute('hidden')
  }

  refreshCta() {
    const link = this.querySelector('[data-anchor-carousel-link]')
    if (link) {
      link.removeEventListener('click', this._onLinkClick)
    }
    if (!link) {
      this.removeAttribute('hidden')
      this.restoreFromSplideCarousel()
      scheduleAnchorAtelierSlidersResync()
      return
    }
    const hasScrollCta =
      link.hasAttribute('data-scroll-target') ||
      link.hasAttribute('data-scroll-target-product-id')
    if (!hasScrollCta) {
      this.removeAttribute('hidden')
      this.restoreFromSplideCarousel()
      scheduleAnchorAtelierSlidersResync()
      return
    }
    link.href = `${window.location.pathname}${window.location.search}`

    if (isAnchorAtelierPlpHidingFullSection()) {
      this.removeAttribute('hidden')
      this.restoreFromSplideCarousel()
      const targetForClick = resolveAnchorCarouselAtelierScrollTarget(link)
      if (targetForClick) {
        link.addEventListener('click', this._onLinkClick)
      }
      scheduleAnchorAtelierSlidersResync()
      return
    }

    const targetEl = resolveAnchorCarouselAtelierScrollTarget(link)
    if (!targetEl) {
      this.hidden = true
      this.omitFromSplideCarousel()
    } else {
      this.removeAttribute('hidden')
      this.restoreFromSplideCarousel()
      link.addEventListener('click', this._onLinkClick)
    }
    scheduleAnchorAtelierSlidersResync()
  }
}

customElements.define(
  'anchor-carousel-atelier-slide',
  AnchorCarouselAtelierSlide
)

if (document.querySelector('[data-anchor-carousel-atelier-root]')) {
  ensureAnchorAtelierLocationChangeEvents()
  syncAnchorAtelierVisibilityAndSlides()
  window.addEventListener('locationchange', syncAnchorAtelierVisibilityAndSlides)
  document.addEventListener('filters-rendered', syncAnchorAtelierVisibilityAndSlides)
}
